import apiClient from './client';
import type { ProduttoreInline } from '../types/produttore';
import type { OmologaInline } from '../types/omologa';
import type { OrderStatus } from '../types';
import type { FatturaRiferimento } from '../lib/modificaFiscale';

// ── Tipi ────────────────────────────────────────────────────────────────────

export interface CustomerSummary {
  id: string;
  ragione_sociale: string;
}

export interface RichiestaListItem {
  id: string;
  codice_ordine: string;
  customer: CustomerSummary;
  categoria: string;
  cer_code: string | null;
  cer_classificazione: string | null;   // della riga CER assegnata (per formatCer)
  conto_energia: string | null;
  modalita_conferimento: string;
  status: OrderStatus | string;
  referente_richiesta: string;
  telefono_richiesta: string;
  email_richiesta: string;
  created_at: string;
  days_pending: number;
  // #107g: tipo consultazione aperta (solo righe in_consultazione), altrimenti null.
  consultazione_tipo?: 'RT' | 'DIR' | null;
  // Stati fatturazione DERIVATI (#97), calcolati lato BE.
  fatturato_ordine?: boolean;
  pagato_ordine?: boolean;
  /** V75 — nota INTERNA dell'ufficio, testo INTERO: in tabella si vede troncata,
   *  ma il tooltip ha bisogno del resto. */
  nota_interna?: string | null;
}

export interface CustomerDetail {
  id: string;
  ragione_sociale: string;
  piva: string | null;
  cf: string | null;
  // Nullability reale della tabella `customers`: questi campi sono nullable a
  // schema e cancellabili dalla UI (R6-B).
  indirizzo: string | null;
  citta: string | null;
  provincia: string | null;
  cap: string | null;
  referente: string | null;
  telefono: string | null;
  email: string;
  pec: string | null;
  modificato_at: string | null;
  modificato_da: string | null;
}

// Consultazione inline nel detail (#96).
export type ConsultazioneTipo = 'RT' | 'DIR';
export type ConsultazioneStato = 'aperta' | 'risposta' | 'chiusa';

export interface ConsultazioneItem {
  id: string;
  order_id: string;
  tipo: ConsultazioneTipo;
  stato: ConsultazioneStato;
  commento_richiesta: string;
  commento_risposta: string | null;
  stato_pre_consultazione: string | null;
  richiesta_at: string;
  richiesta_da: string | null;
  richiesta_da_nome: string | null;
  risposta_at: string | null;
  risposta_da: string | null;
  risposta_da_nome: string | null;
}

export interface RichiestaDetail {
  id: string;
  codice_ordine: string;
  customer: CustomerDetail;
  status: OrderStatus | string;
  categoria: string;
  cer_attivo_id: string | null;
  cer_code: string | null;
  cer_classificazione: string | null;   // della riga CER assegnata (per formatCer)
  conto_energia: string | null;
  produttore_coincide: boolean;
  referente_richiesta: string;
  email_richiesta: string;
  telefono_richiesta: string;
  origine_indirizzo: string | null;
  origine_comune: string | null;
  origine_provincia: string | null;
  origine_cap: string | null;
  gse_codice_impianto: string | null;
  gse_censimp: string | null;
  gse_conto_energia: string | null;
  gse_potenza_kw: number | null;
  gse_data_esercizio: string | null;
  gse_causale: string | null;
  gse_sogg_responsabile: boolean | null;
  gse_intestatario: string | null;
  gse_piva_cf: string | null;
  frequenza_conferimento: string | null;
  stato_materiale: string | null;
  modalita_conferimento: string;
  pv_n_pannelli: number | null;
  pv_marca: string | null;
  pv_modello: string | null;
  pv_smontati: boolean | null;
  pv_imballaggio: string | null;
  pv_vetri_rotti: boolean | null;
  serr_materiale: string | null;
  serr_peso_kg: number | null;
  serr_n_infissi: number | null;
  cavi_peso_kg: number | null;
  cavi_tipologia: string | null;
  cavi_pct_rame: number | null;
  tras_noto: boolean | null;
  tras_ragione_sociale: string | null;
  tras_anga: string | null;
  tras_note_logistiche: string | null;
  note_cliente: string | null;
  processo_generazione: string | null;   // R2-E
  created_at: string;
  days_pending: number;
  modificato_at: string | null;
  modificato_da: string | null;
  produttore: ProduttoreInline | null;

  // Annullamento (mantenuto su orders — V25 stop-and-ask "a")
  annullato_at: string | null;
  annullato_da: string | null;
  annullato_da_nome: string | null;
  motivazione_annullamento: string | null;

  // Parametri preventivo (V12, rinominato in V25)
  tariffa_unitaria_eur_kg: number | null;
  validita_preventivo_giorni: number | null;
  note_preventivo: string | null;
  costo_trasporto_eur_per_viaggio: number | null;
  numero_trasporti_stimati: number | null;
  peso_stimato_kg: number | null;

  // Direzione operazione (V43) + default categoria (prompt 4) per il prefill preventivo
  tipo_operazione: string | null;
  categoria_tariffa_default: number | null;
  categoria_tipo_operazione_default: string | null;

  // Audit transizioni nuovo flusso
  preventivo_inviato_at: string | null;
  preventivo_inviato_da: string | null;

  preventivo_confermato_at: string | null;
  preventivo_confermato_da: string | null;

  omologa_inviata_at: string | null;
  omologa_inviata_da: string | null;

  omologa_ricevuta_at: string | null;
  omologa_ricevuta_da: string | null;
  omologa_produttore_data_firma: string | null;
  omologa_produttore_data_scadenza: string | null;

  // Scheda omologa (V13)
  scheda_omologa_numero_conferimenti: number | null;
  scheda_omologa_frequenza: string | null;
  scheda_omologa_pezzatura: string | null;
  scheda_omologa_note: string | null;
  scheda_omologa_trasportatori: unknown[] | null;
  scheda_omologa_intermediari: unknown[] | null;

  // Omologa associata all'ordine (Blocco 6: entità riutilizzabile).
  // null se nessuna omologa associata via genera-preventivo-omologa.
  omologa: OmologaInline | null;

  // Logistica cliente (V20)
  cliente_porta_materiale: boolean;

  // Stati fatturazione DERIVATI (#97).
  fatturato_ordine?: boolean;
  pagato_ordine?: boolean;
  // Contatori conferimenti (#124).
  conferimenti_totali?: number;
  conferimenti_fatturati?: number;
  conferimenti_pagati?: number;

  // Consultazioni inline (#96)
  consultazioni: ConsultazioneItem[];

  /**
   * Avviso NON bloccante valorizzato dal solo PATCH quando la modifica del
   * blocco `customer` tocca dati fiscali e il cliente ha bozze fattura
   * aperte. Sempre null sulle GET.
   */
  avviso_bozze_aperte?: FatturaRiferimento[] | null;

  /** R6-C: traccia della riassegnazione (V66), presente su OGNI GET. Alimenta il badge. */
  cliente_precedente?: ClientePrecedenteInline | null;
  /** R6-C: avvisi non bloccanti, solo nella risposta al PATCH /cliente. */
  avvisi_riassegnazione?: AvvisoRiassegnazione[] | null;
  /** V68: traccia della revoca della conferma, presente su OGNI GET. Alimenta il badge. */
  revoca?: RevocaInline | null;

  /** Storico revisioni tariffa (quote_revisions), presente su OGNI GET. Alimenta la cronologia. */
  revisioni_tariffa?: RevisioneTariffaItem[];

  /** V75 — nota INTERNA dell'ufficio: una sola, condivisa fra richiesta e ordine.
   *  `updated_at`/`updated_by_nome` restano valorizzati anche a nota svuotata:
   *  chi l'ha cancellata e quando e' l'informazione che serve dopo. */
  nota_interna?: string | null;
  nota_interna_updated_at?: string | null;
  nota_interna_updated_by_nome?: string | null;
}

/**
 * Riga di quote_revisions nel dettaglio. Gli importi arrivano da NUMERIC(10,2):
 * tariffe con più di 2 decimali sono arrotondate qui (non su orders).
 */
export interface RevisioneTariffaItem {
  importo_precedente: number | null;
  importo_nuovo: number | null;
  revised_at: string;
  revised_da_nome: string | null;
  note: string | null;
}

/** I campi interni restano `riaperta_*`: proiezione delle colonne V68. */
export interface RevocaInline {
  riaperta_at: string;
  riaperta_da: string | null;
  riaperta_da_nome: string | null;
}

export interface ClientePrecedenteInline {
  id: string;
  ragione_sociale: string;
  cambiato_at: string;
  cambiato_da: string | null;
  cambiato_da_nome: string | null;
}

export interface AvvisoRiassegnazione {
  codice:
    | 'documenti_cliente_precedente'
    | 'produttore_non_aggiornato'
    | 'conferimenti_staccati_da_bozza';
  dettagli: string[];
}

// ── Tipi per PATCH ───────────────────────────────────────────────────────────

/**
 * Blocco `customer` del PATCH richiesta. Il backend usa `exclude_unset`:
 * chiave ASSENTE = campo invariato, chiave a `null` = cancellazione esplicita.
 * Vanno quindi inviate esplicitamente tutte le chiavi annullabili, anche
 * quelle non modificate.
 *
 * `ragione_sociale` ed `email` sono NOT NULL a schema: si inviano solo se
 * valorizzate, mai a `null` (il backend risponderebbe 422).
 *
 * Nota naming: qui il codice fiscale è `cf`, mentre PATCH /customers espone
 * `codice_fiscale`. Divergenza preesistente fra i due percorsi.
 */
export interface CustomerUpdatePayload {
  ragione_sociale?: string;
  piva?: string | null;
  cf?: string | null;
  indirizzo?: string | null;
  citta?: string | null;
  provincia?: string | null;
  cap?: string | null;
  referente?: string | null;
  email?: string;
  telefono?: string | null;
  pec?: string | null;
  /** Conferma della modifica dei dati fiscali su cliente già fatturato (409). */
  conferma_modifica_fiscale?: boolean;
}

export interface RichiestaUpdatePayload {
  customer?: CustomerUpdatePayload;
  cer_attivo_id?: string;   // V48: riga CER d'ingresso (il BE sincronizza cer_code)
  cer_code?: string;
  /**
   * #116 — conferma dello spostamento a ledger dei kg già a saldo sotto il CER
   * attuale. Top-level (non dentro `customer`, dove sta invece la conferma
   * fiscale): la guardia è sull'ordine, non sull'anagrafica.
   */
  conferma_propagazione_ledger?: boolean;
  conto_energia?: 'si' | 'no' | 'non_so';
  produttore_coincide?: boolean;
  origine_indirizzo?: string;
  origine_comune?: string;
  origine_provincia?: string;
  origine_cap?: string;
  frequenza_conferimento?: 'una_tantum' | 'mensile' | 'trimestrale' | 'da_definire';
  stato_materiale?: 'integro' | 'parzialmente_danneggiato' | 'rotto';
  modalita_conferimento?: 'porto' | 'ritiro' | 'nd';
  // Peso complessivo stimato dell'ordine (order-level), compilato dall'operatore.
  peso_stimato_kg?: number;
  gse_codice_impianto?: string;
  gse_censimp?: string;
  gse_conto_energia?: 'I' | 'II' | 'III' | 'IV' | 'V';
  gse_potenza_kw?: number;
  gse_data_esercizio?: string;
  gse_causale?: 'fine_vita' | 'revamping' | 'danno' | 'smontaggio' | 'altro';
  gse_sogg_responsabile?: boolean;
  gse_intestatario?: string;
  gse_piva_cf?: string;
  pv_n_pannelli?: number;
  pv_marca?: string;
  pv_modello?: string;
  pv_smontati?: boolean;
  pv_imballaggio?: 'bancali' | 'big_bags' | 'sfusi' | 'da_definire';
  pv_vetri_rotti?: boolean;
  serr_materiale?: 'pvc' | 'alluminio' | 'misto';
  serr_peso_kg?: number;
  serr_n_infissi?: number;
  cavi_peso_kg?: number;
  cavi_tipologia?: 'rame_puro' | 'rame_pvc' | 'misto';
  cavi_pct_rame?: number;
  tras_noto?: boolean;
  tras_ragione_sociale?: string;
  tras_anga?: string;
  tras_note_logistiche?: string;
  note_cliente?: string;
  processo_generazione?: string;   // R2-E
}

// ── API: list/get/update ────────────────────────────────────────────────────

export interface RichiesteFilters {
  status?: string;
  categoria?: string;
  days_from?: number;
  customer_id?: string;
  produttore_id?: string;
  consultazione_tipo?: 'RT' | 'DIR';
  // Filtro stato fatturazione derivato (#97).
  fatturazione?: 'fatturato' | 'pagato';
}

export async function getRichieste(filters?: RichiesteFilters): Promise<RichiestaListItem[]> {
  const params: Record<string, string> = {};
  if (filters?.status) params['status'] = filters.status;
  if (filters?.categoria) params['categoria'] = filters.categoria;
  if (filters?.days_from != null) params['days_from'] = String(filters.days_from);
  if (filters?.customer_id) params['customer_id'] = filters.customer_id;
  if (filters?.produttore_id) params['produttore_id'] = filters.produttore_id;
  if (filters?.consultazione_tipo) params['consultazione_tipo'] = filters.consultazione_tipo;
  if (filters?.fatturazione) params['fatturazione'] = filters.fatturazione;
  const { data } = await apiClient.get<RichiestaListItem[]>('/api/richieste', { params });
  return data;
}

export async function getRichiestaById(id: string): Promise<RichiestaDetail> {
  const { data } = await apiClient.get<RichiestaDetail>(`/api/richieste/${id}`);
  return data;
}

export async function updateRichiesta(
  id: string,
  payload: RichiestaUpdatePayload,
): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(`/api/richieste/${id}`, payload);
  return data;
}

// ── R5/F6: foto del materiale ──────────────────────────────────────────────

export interface FotoItem {
  id: string;
  ordinamento: number;
  mime: string;
  created_at: string;
}

/** Upload di una foto PRIMA del submit (canale pubblico/pre-collegamento). Ritorna
 *  il photo_id da includere in foto_ids. Da autenticato passa senza rate-limit. */
export async function uploadFotoPreSubmit(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await apiClient.post<{ photo_id: string }>('/api/richieste/foto', fd);
  return data.photo_id;
}

export async function listFotoOrdine(ordineId: string): Promise<FotoItem[]> {
  const { data } = await apiClient.get<FotoItem[]>(`/api/richieste/${ordineId}/foto`);
  return data;
}

/** Bytes della foto (serving autenticato) → object URL per un <img>. Il chiamante
 *  deve revocare l'URL con URL.revokeObjectURL quando non serve più. */
export async function getFotoObjectUrl(ordineId: string, photoId: string): Promise<string> {
  const { data } = await apiClient.get<Blob>(`/api/richieste/${ordineId}/foto/${photoId}`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(data);
}

export async function uploadFotoOrdine(ordineId: string, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await apiClient.post<{ photo_id: string }>(`/api/richieste/${ordineId}/foto`, fd);
  return data.photo_id;
}

export async function deleteFotoOrdine(ordineId: string, photoId: string): Promise<void> {
  await apiClient.delete(`/api/richieste/${ordineId}/foto/${photoId}`);
}

export async function countRichieste(status = 'ricevuto'): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/api/richieste/count', {
    params: { status },
  });
  return data.count;
}

// Contatore tab derivato (esclusivo): fatturato = fatturato AND NOT pagato.
export async function countRichiesteDerivato(fatturazione: 'fatturato' | 'pagato'): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/api/richieste/count', {
    params: { fatturazione },
  });
  return data.count;
}

export async function countRichiesteAttive(): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/api/richieste/count-attive');
  return data.count;
}

export async function countOrdiniAttivi(): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/api/richieste/count-ordini-attivi');
  return data.count;
}

export interface CambiaProduttorePayload {
  produttore_id: string | null;
  produttore_coincide: boolean;
}

export async function cambiaProduttoreRichiesta(
  id: string,
  payload: CambiaProduttorePayload,
): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(`/api/richieste/${id}/produttore`, payload);
  return data;
}

// ── R6-C — riassegnazione del cliente dell'ordine (#62) ─────────────────────

export interface CambiaClientePayload {
  customer_id: string;
  /**
   * R6-D — il backend distingue chiave ASSENTE (campo invariato) da chiave a
   * null/vuota. `referente_richiesta` e' nullable a schema: `null` lo cancella.
   * `email`/`telefono` sono NOT NULL: vuoti o null danno 422.
   * La UI invia sempre tutte e tre le chiavi.
   */
  referente_richiesta?: string | null;
  email_richiesta?: string;
  telefono_richiesta?: string;
  /** Sblocca la sola guardia bozza; i bloccanti su fatture non sono aggirabili. */
  conferma_riassegnazione?: boolean;
}

/** Riferimento a una fattura attiva nel 409 bloccante. */
export interface FatturaAttivaRef {
  id: string;
  numero: string | null;
  data: string | null;
  stato: string;
}

/** Riferimento a una fattura passiva nel 409 bloccante. */
export interface FatturaPassivaRef {
  id: string;
  numero: string | null;
  data: string | null;
  pagata: boolean;
}

export type CambiaClienteConflitto =
  | { code: 'fatture_attive_collegate'; message: string; fatture: FatturaAttivaRef[] }
  | { code: 'fatture_passive_collegate'; message: string; fatture_passive: FatturaPassivaRef[] }
  | { code: 'conferma_riassegnazione_richiesta'; message: string; conferimenti: string[] };

/**
 * Estrae il detail strutturato di un 409 dell'endpoint di riassegnazione.
 * Ritorna null per qualunque altro errore.
 */
export function parseCambiaClienteConflitto(err: unknown): CambiaClienteConflitto | null {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const code = (detail as { code?: string }).code;
  if (
    code === 'fatture_attive_collegate' ||
    code === 'fatture_passive_collegate' ||
    code === 'conferma_riassegnazione_richiesta'
  ) {
    return detail as CambiaClienteConflitto;
  }
  return null;
}

export async function cambiaClienteRichiesta(
  id: string,
  payload: CambiaClientePayload,
): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(`/api/richieste/${id}/cliente`, payload);
  return data;
}

// Modello C — materializza il produttore dai dati del cliente dell'ordine.
// Tutti i campi sono override opzionali: quelli omessi sono derivati dal cliente
// lato server (email←email, note←referente, tipo_soggetto inferito).
export interface ProduttoreDaClientePayload {
  ragione_sociale?: string;
  piva?: string;
  codice_fiscale?: string;
  email?: string;
  telefono?: string;
  indirizzo?: string;
  citta?: string;
  provincia?: string;
  cap?: string;
  note?: string;
  tipo_soggetto?: 'azienda' | 'privato' | 'ente_pubblico';
}

// #106 — conclusione manuale ordine (in_progress → concluso).
export async function concludiOrdine(id: string): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(`/api/richieste/${id}/concludi`);
  return data;
}

// Riapertura ordine concluso (concluso → in_progress). Inverso del concludi: non
// tocca conferimenti né fatture (restano intatti).
/**
 * V68 — revoca la conferma: `confermato → inviato_cliente`.
 * Deprecia TUTTI i documenti dell'ordine e sgancia l'omologa. Da qui si
 * prosegue con `riporta-a-generazione` e la rigenerazione, gia' esistenti.
 */
export async function revocaConferma(id: string): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(`/api/richieste/${id}/revoca-conferma`);
  return data;
}

export async function riapriOrdine(id: string): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(`/api/richieste/${id}/riapri`);
  return data;
}

/** Blocco trasporto: scrittura sempre accoppiata (abilita ⇔ costo, lato BE). */
export interface TrasportoCondizioniPayload {
  abilita: boolean;
  costo_per_viaggio?: number | null;
}

export interface ModificaCondizioniPayload {
  tariffa_unitaria_eur_kg?: number;
  trasporto?: TrasportoCondizioniPayload;
  /** Conferma della modifica con preventivo firmato vivo (sblocca il 409). */
  conferma_modifica_condizioni?: boolean;
}

/**
 * Correzione condizioni economiche post-conferma (tariffa e/o trasporto, stati
 * confermato/in_progress/concluso): la via per quando il flusso documentale
 * (revoca-conferma) e' precluso dai dati a valle. Solo ordini di SERVIZIO:
 * sugli acquisti risponde 422 (le condizioni si gestiscono dal prospetto
 * fornitore). Con preventivo firmato risponde 409
 * `conferma_modifica_condizioni_richiesta` (detail con i soli blocchi che
 * cambiano), sbloccabile col flag nel payload. La risposta puo' elencare in
 * `avviso_bozze_aperte` le bozze che ricalcolano live i totali.
 */
export async function modificaCondizioniEconomiche(
  id: string,
  payload: ModificaCondizioniPayload,
): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(
    `/api/richieste/${id}/condizioni-economiche`,
    payload,
  );
  return data;
}

/**
 * V75 — scrive la nota INTERNA dell'ufficio. Endpoint dedicato e non un campo di
 * `updateRichiesta`: quella PATCH usa `exclude_none` lato BE, quindi svuotare la
 * nota sarebbe inesprimibile. Stringa vuota = svuota (il BE la porta a NULL e
 * riscrive comunque autore e data).
 */
export async function aggiornaNotaInterna(
  id: string,
  testo: string,
): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(
    `/api/richieste/${id}/nota-interna`, { testo },
  );
  return data;
}

// #7: inviato_cliente → preventivo_omologa_generati (passo indietro per rigenerare).
export async function riportaAGenerazione(id: string): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(`/api/richieste/${id}/riporta-a-generazione`);
  return data;
}

// #8: invio della sola scheda omologa al produttore (azione autonoma, no cambio stato).
export async function inviaOmologaProduttore(id: string): Promise<RichiestaDetail> {
  const { data } = await apiClient.post<RichiestaDetail>(`/api/richieste/${id}/invia-omologa-produttore`);
  return data;
}

export async function produttoreDaClienteRichiesta(
  id: string,
  payload: ProduttoreDaClientePayload,
): Promise<RichiestaDetail> {
  const { data } = await apiClient.post<RichiestaDetail>(
    `/api/richieste/${id}/produttore-da-cliente`,
    payload,
  );
  return data;
}

// ── Transizioni nuovo flusso (#95) ──────────────────────────────────────────

// Dati per creare una nuova omologa inline (ramo modalita_omologa='nuova').
// Allineato a NuovaOmologaInput del backend.
export interface NuovaOmologaPayload {
  // numero_omologa autogenerato per produttore (#134): non si invia più.
  numero_conferimenti?: number | null;
  frequenza?: string | null;
  pezzatura?: string | null;
  note?: string | null;
  data_firma?: string | null;
  data_scadenza?: string | null;
  trasportatori_ids?: string[];
  intermediari_ids?: string[];
}

export interface GeneraPreventivoOmologaPayload {
  // Preventivo (sempre)
  tariffa_unitaria_eur_kg: number;
  costo_trasporto_eur_per_viaggio?: number | null;
  numero_trasporti_stimati?: number | null;
  validita_preventivo_giorni?: number;
  note_preventivo?: string | null;
  cliente_porta_materiale?: boolean;
  tipo_operazione?: 'servizio' | 'acquisto';
  // Discriminante omologa (Blocco 6 — refactor sotto-commit STEP 2-2)
  modalita_omologa: 'esistente' | 'nuova';
  omologa_id?: string | null;
  omologa_nuova?: NuovaOmologaPayload | null;
}

export async function generaPreventivoOmologa(
  richiestaId: string,
  payload: GeneraPreventivoOmologaPayload,
): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(
    `/api/richieste/${richiestaId}/genera-preventivo-omologa`,
    payload,
  );
  return data;
}

export type AllegatiInvio = 'preventivo' | 'omologa' | 'entrambi' | 'nessuno';

export interface InviaClientePayload {
  email_to?: string | null;
  email_cc?: string[] | null;
  email_subject?: string | null;
  email_body?: string | null;
  force_send_failed?: boolean;
  inviato_manualmente?: boolean;
  allegati?: AllegatiInvio;
}

export type EmailActionResponse = RichiestaDetail & {
  email_sent: boolean;
  email_warning: string | null;
};

export async function inviaCliente(
  richiestaId: string,
  payload: InviaClientePayload,
): Promise<EmailActionResponse> {
  const { data } = await apiClient.patch<EmailActionResponse>(
    `/api/richieste/${richiestaId}/invia-cliente`,
    payload,
  );
  return data;
}

export async function confermaRichiesta(
  richiestaId: string,
  file: File,
  note_conferma?: string | null,
): Promise<RichiestaDetail> {
  const formData = new FormData();
  formData.append('file', file);
  if (note_conferma) formData.append('note_conferma', note_conferma);
  const { data } = await apiClient.patch<RichiestaDetail>(
    `/api/richieste/${richiestaId}/conferma`,
    formData,
  );
  return data;
}

// Modello Z (TD-2): se l'omologa associata è già firmata, file e dataFirma sono
// opzionali (la transizione usa il firmato esistente). Caricare un file è una
// sostituzione esplicita del firmato canonico dell'omologa.
export async function passaInProgress(
  richiestaId: string,
  file: File | null,
  dataFirma: string | null,
): Promise<RichiestaDetail> {
  const formData = new FormData();
  if (file) formData.append('file', file);
  if (dataFirma) formData.append('data_firma', dataFirma);
  const { data } = await apiClient.patch<RichiestaDetail>(
    `/api/richieste/${richiestaId}/in-progress`,
    formData,
  );
  return data;
}

export async function annullaRichiesta(
  richiestaId: string,
  motivazione: string,
): Promise<RichiestaDetail> {
  const { data } = await apiClient.patch<RichiestaDetail>(
    `/api/richieste/${richiestaId}/annulla`,
    { motivazione },
  );
  return data;
}

// ── Crea richiesta (form pubblico, stato iniziale "ricevuto") ───────────────

interface CreateRichiestaPayload {
  ragione_sociale: string;
  email: string;
  referente: string;
  telefono?: string;
  piva?: string;
  cf?: string;
  categorie: string[];
  dichiarazione_accettata: boolean;
  gdpr_consenso: boolean;
}

interface CreateRichiestaResponse {
  order_ids: string[];
  codici_ordine: string[];
  customer_id: string;
  stato: string;
  messaggio: string;
}

export async function createRichiestaAPI(
  payload: CreateRichiestaPayload,
): Promise<CreateRichiestaResponse> {
  const { data } = await apiClient.post<CreateRichiestaResponse>('/api/richieste', payload);
  return data;
}
